import { READER_WAITING_LABEL } from '@/components/reader';
import {
  type ConversationBubble,
  type ThreadEntry,
  type ThreadSlot,
  isThreadSlot,
} from '@/lib/session-conversation';
import type { SessionPersona, SessionProtocolState } from '@matter_extractor/shared';
import { SESSION_MESSAGE_MAX_CHARS, assertNever } from '@matter_extractor/shared';
import { LoaderCircle } from 'lucide-react'; // pms-design-exception: person-side screen painted by the experience theme, stroke icons kept as drawn
import type { CSSProperties, FormEvent, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { SESSION_TEST_IDS } from './contracts';

/**
 * THE CONVERSATION of the protocol page (§9.5), in the person's browser.
 *
 * It takes the place the seam of #60 held - « à la même place dans le lecteur,
 * sur le même canal et la même table » - and `protocol-seam.tsx` went with it:
 * `sessionProtocolMode` stopped answering `seam` when #64 landed, so the card
 * had already become a component nothing could mount.
 *
 * ## What the person sees, and what they must NOT see
 *
 * « Un rendu en bulles, habillées par les tokens du thème, dans la colonne de
 * lecture », the interviewer on the left with the FIRST NAME and the avatar of
 * a **named** persona and nothing at all for an anonymous one (§10.1), the
 * person on the right, and « aucun indicateur de progression : ni numéro
 * d'étape, ni compteur de tours, ni statut d'objectif, ni minuteur ». That last
 * sentence is a design rule and a security rule at once: a step number on
 * screen is a step number the person reads their answers against, and the
 * interviewer is written never to say where it is aiming (H32, §8.7). This
 * component is given a thread and a state, and there is deliberately nothing
 * else in its props to leak.
 *
 * ## Painted in the THEME, never in PMS tokens
 *
 * Every colour, face and shape below is a `--lecteur-*` property, the ones
 * `reader-theme.ts` writes on the reader's root and the frame of
 * `session-player.tsx` re-poses around this component. The seam said why and it
 * is unchanged: this stands inside the window the theme paints, and a PMS-blue
 * bubble on a navy experience would be the one thing on the page that is not
 * the experience's. The values are inline because `reader.css` is the reader's
 * own stylesheet, which this component is not part of.
 *
 * **One exception, and it is deliberate.** A bubble's radius is a FIXED 14 px
 * and not `var(--lecteur-button-radius)`. The three shapes of §9.4 are BUTTON
 * shapes: `pill` is 999 px, which on a three-line bubble draws a lozenge rather
 * than a message. The send button and « Continuer » do take the token - they
 * are buttons, and that is exactly what the setting is for.
 *
 * ## The ends (§9.5)
 *
 * `closed` - the protocol is over: the last message stays on screen and
 * « Continuer » appears under it. Never an automatic hand-over: « jamais de
 * bascule automatique au milieu d'une lecture ». `experience-closed` - the
 * session is closed and `session-player.tsx` draws the ending of §5.3 over the
 * whole window, so the footer simply empties rather than offering a gesture
 * that leads nowhere.
 *
 * `farewell` - the goodbye of a farewell step is being written (issue #163): the
 * composer is REMOVED, not greyed - « personne n'a sous les yeux une saisie qui
 * l'inviterait à répondre à un au revoir » -, the goodbye streams in its bubble,
 * and the footer stays empty until `closed` brings « Continuer » under it. A
 * reprise during the goodbye reopens on this state (H80), so the same footer.
 *
 * ## The pages played in the thread (prolongation.md §5, issue #186)
 *
 * `page` - the session stands on a page played in the conversation: the
 * footer is EMPTY, there is no field and nothing to type. The page's bubble is
 * written like any message, and the elements of the page - drawn by the caller,
 * `renderItems` - hang under it, aligned on the column of the interviewer's
 * bubbles, after the avatar. What the person clicks comes back as THEIR bubble
 * (a card taken is quoted and cut to four lines, a Bouton's label is a plain
 * bubble). « Continuer » comes only after the goodbye, as on any protocol.
 */

export interface ConversationChannel {
  /**
   * The floor and the tail, already assembled by `conversationThread`, with the
   * elements of the pages of the thread laid among them (`conversationEntries`).
   */
  readonly thread: readonly ThreadEntry[];
  /**
   * Draws the elements of one page of the thread. Absent, a slot draws nothing -
   * the protocol page has none.
   */
  readonly renderItems?: (slot: ThreadSlot) => ReactNode;
  readonly typing: boolean;
  readonly state: SessionProtocolState;
  /** A turn is in flight: the composer is inert until the answer lands. */
  readonly busy: boolean;
  /** A refusal of the last message, WORDED FROM ITS CODE by the route. */
  readonly refusal: string | null;
  /** Hands the text to `POST /message`. Answers false when it was refused. */
  readonly onSend: (text: string) => Promise<boolean>;
}

export interface ProtocolConversationProps {
  /** The persona of the frozen version, or null - an anonymous one shows nothing. */
  readonly persona: SessionPersona | null;
  readonly channel: ConversationChannel;
  /** « Continuer » - the advance off the protocol page. */
  readonly onContinue: () => void;
  /**
   * An advance is on its way - or WAITING on the determination of the noyau
   * (noyau.md §4.6, N6): « Continuer » then reads « Un instant... » for as long
   * as it takes, and the session does not move.
   */
  readonly advancing: boolean;
  /**
   * The session is OVER on this page (noyau.md §5.3, N27): the skip of the noyau
   * found nothing after the protocol page, so it ended here. Neither the
   * composer nor « Continuer » is offered - the last message stays, and an
   * advance could only be refused.
   */
  readonly finished?: boolean;
  /**
   * « Regarder » (npc.md §11): the sentence the field carries while an operator WATCHES this
   * conversation. The composer is drawn as the person has it, but inert - nothing is typed, nothing
   * is sent - and « Continuer » stays where it is without answering.
   */
  readonly watching?: string;
  /** « Réduire les animations » (§9.4): no pulse, and no smooth scroll. */
  readonly reducedMotion: boolean;
}

/** « Entrée envoie · Maj+Entrée va à la ligne », the line §9.5 asks for word for word. */
const COMPOSER_HINT = 'Entrée envoie · Maj+Entrée va à la ligne';

const SHELL: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  color: 'var(--lecteur-color-text)',
  fontFamily: 'var(--lecteur-font-text)',
  fontSize: 'var(--lecteur-normal-size)',
  lineHeight: 'var(--lecteur-normal-line)',
};

/** The reading column of §9.4 - the same ruler the reader lays its blocks on. */
const COLUMN: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: 'var(--lecteur-column)',
  margin: '0 auto',
  minWidth: 0,
};

const HEAD: CSSProperties = {
  flex: '0 0 auto',
  padding: 'var(--lecteur-pad-inline)',
  paddingBottom: '0',
};

const LOG: CSSProperties = {
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: 'var(--lecteur-pad-block) var(--lecteur-pad-inline)',
};

const FOOT: CSSProperties = {
  flex: '0 0 auto',
  padding: 'var(--lecteur-pad-inline)',
  paddingTop: '0',
};

const AVATAR: CSSProperties = {
  display: 'inline-flex',
  flex: '0 0 auto',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '999px',
  backgroundColor: 'var(--lecteur-color-accent)',
  color: 'var(--lecteur-color-button-text)',
  fontSize: 'var(--lecteur-small-size)',
  fontWeight: 600,
};

const NAME: CSSProperties = {
  color: 'var(--lecteur-color-muted)',
  fontSize: 'var(--lecteur-small-size)',
  lineHeight: 'var(--lecteur-small-line)',
};

const BUBBLE: CSSProperties = {
  margin: 0,
  padding: '12px 16px',
  borderRadius: '14px',
  // A message is the one place a person's own line breaks must survive, and a
  // long word without a space must never widen the window (§9.5 asks for zero
  // overflow on a phone).
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};

const AI_BUBBLE: CSSProperties = {
  ...BUBBLE,
  backgroundColor: 'var(--lecteur-color-ai-bubble)',
  borderBottomLeftRadius: '6px',
};

const PERSON_BUBBLE: CSSProperties = {
  ...BUBBLE,
  backgroundColor: 'var(--lecteur-color-person-bubble)',
  borderBottomRightRadius: '6px',
};

const AI_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '8px',
  alignSelf: 'flex-start',
  maxWidth: '92%',
  minWidth: 0,
};

const PERSON_ROW: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'flex-end',
  maxWidth: '85%',
  minWidth: 0,
};

/**
 * What a page of the thread brings, under the bubble: aligned on the column of
 * the interviewer's bubbles, after the avatar (32 px and the 8 px gap) when the
 * persona is named - the planche's `margin-left: 40px`.
 */
const ITEMS: CSSProperties = { minWidth: 0 };
const ITEMS_INSET = '40px';

/** A card taken, quoted: a rule in the accent, a smaller size, four lines at most. */
const QUOTE: CSSProperties = {
  fontSize: 'var(--lecteur-small-size)',
  lineHeight: 'var(--lecteur-small-line)',
  borderLeft: '3px solid var(--lecteur-color-accent)',
};

const CLAMP: CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const STACK: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 };

const FIELD: CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: '72px',
  resize: 'none',
  padding: '12px 16px',
  border: 'none',
  borderRadius: '14px',
  // The person's own colour for the person's own field: it is the only surface
  // on the page whose words are theirs before they are anybody's.
  backgroundColor: 'var(--lecteur-color-person-bubble)',
  color: 'var(--lecteur-color-text)',
  fontFamily: 'var(--lecteur-font-text)',
  fontSize: 'var(--lecteur-normal-size)',
  lineHeight: 'var(--lecteur-normal-line)',
};

const BUTTON: CSSProperties = {
  flex: '0 0 auto',
  height: 'var(--lecteur-button-height)',
  padding: '0 var(--lecteur-button-pad)',
  borderRadius: 'var(--lecteur-button-radius)',
  backgroundColor: 'var(--lecteur-color-button)',
  color: 'var(--lecteur-color-button-text)',
  fontFamily: 'var(--lecteur-font-text)',
  fontSize: 'var(--lecteur-button-font)',
  fontWeight: 600,
  cursor: 'pointer',
};

const HINT: CSSProperties = {
  margin: '8px 0 0',
  color: 'var(--lecteur-color-muted)',
  fontSize: 'var(--lecteur-small-size)',
  lineHeight: 'var(--lecteur-small-line)',
};

/** « Un instant... » and its small loading icon, on one line. */
const WAITING: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '8px' };

export function ProtocolConversation({
  persona,
  channel,
  onContinue,
  advancing,
  finished = false,
  watching,
  reducedMotion,
}: ProtocolConversationProps) {
  const firstName = persona?.kind === 'named' ? persona.firstName : null;
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  /*
    « Un défilement qui suit le dernier message » (§9.5).

    The trigger carries the LENGTH of the last bubble and not only the count:
    the interviewer's answer grows inside one bubble as the deltas land, so a
    thread keyed on its own length would stop following at the first word. The
    effect reads none of it - it is the trigger, the régime of `chat/thread.tsx`.
  */
  const lastBubble = [...channel.thread]
    .reverse()
    .find((entry): entry is ConversationBubble => !isThreadSlot(entry));
  const follow = `${channel.thread.length}:${lastBubble?.text.length ?? 0}:${channel.typing}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger, not read.
  useEffect(() => {
    // Optional call: jsdom has no `scrollIntoView`.
    endRef.current?.scrollIntoView?.({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'end',
    });
  }, [follow, reducedMotion]);

  // A finished session is not a conversation to answer, whatever the channel
  // last said: a reload of it opens on `step` until the frames arrive (N27).
  const foot = finished ? 'nothing' : footOf(channel.state);
  const open = foot === 'composer';
  const blocked = channel.busy || !open || watching !== undefined;

  /* The hand-back: the moment the turn is no longer blocked, the caret should
     already be in the field - answering never costs a click into it. */
  useEffect(() => {
    // `preventScroll` keeps this from fighting the scroll-to-last-message
    // effect above: the field never needs a scroll to be revealed, it sits in
    // a footer of its own, always on screen.
    if (!blocked) textareaRef.current?.focus({ preventScroll: true });
  }, [blocked]);

  async function send(): Promise<void> {
    const text = draft.trim();
    if (text === '' || blocked) return;
    // Emptied at once so the thread reads like a conversation, and PUT BACK on
    // a refusal: the words are the person's, and a composer that swallowed
    // them on a 409 would make them retype what the server never took.
    setDraft('');
    const accepted = await channel.onSend(text);
    if (!accepted) setDraft(text);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void send();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Without this the newline lands in the draft as well as sending it, and
      // the field she gets back has a blank first line in it.
      event.preventDefault();
      void send();
    }
  }

  return (
    <div data-testid={SESSION_TEST_IDS.conversation} style={SHELL}>
      {firstName !== null && (
        <header style={HEAD}>
          <div style={{ ...COLUMN, flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
            <span style={AVATAR} aria-hidden="true">
              {firstName.slice(0, 1).toUpperCase()}
            </span>
            <span style={{ fontWeight: 600 }}>{firstName}</span>
          </div>
        </header>
      )}

      <div style={LOG} role="log" aria-live="polite" aria-label="La conversation">
        {/* The thread hangs from the BOTTOM: a conversation of two bubbles
            floating at the top of a 800 px window, half a screen above the
            field it answers, reads as a page that failed to load. The pair
            `minHeight: 100%` + `justify-content: flex-end` is what does it, and
            it costs nothing once the thread is long enough to scroll. */}
        <div style={{ ...COLUMN, gap: '14px', minHeight: '100%', justifyContent: 'flex-end' }}>
          {channel.thread.map((entry) =>
            isThreadSlot(entry) ? (
              <div
                key={entry.key}
                style={firstName === null ? ITEMS : { ...ITEMS, marginLeft: ITEMS_INSET }}
              >
                {channel.renderItems?.(entry)}
              </div>
            ) : (
              <Bubble key={entry.key} bubble={entry} firstName={firstName} />
            ),
          )}
          {channel.typing && (
            <div style={AI_ROW} data-testid={SESSION_TEST_IDS.typing}>
              {firstName !== null && (
                <span style={AVATAR} aria-hidden="true">
                  {firstName.slice(0, 1).toUpperCase()}
                </span>
              )}
              {/* An `<output>` and not a `<p role="status">`: the element
                  carries the role natively, which is what Biome's
                  `useSemanticElements` asks for, and it is the only thing in
                  the thread that is a live region of its own - the dots are
                  the answer to « votre message est bien parti ». */}
              <output
                style={{ ...AI_BUBBLE, display: 'block' }}
                aria-label={`${firstName ?? 'Votre interlocuteur'} écrit une réponse`}
              >
                <span aria-hidden="true" className={reducedMotion ? undefined : 'animate-pulse'}>
                  •••
                </span>
              </output>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      <footer style={FOOT}>
        <div style={COLUMN}>
          {channel.refusal !== null && (
            <p role="alert" style={{ ...HINT, margin: '0 0 8px' }}>
              {channel.refusal}
            </p>
          )}
          {open && (
            <>
              <form
                onSubmit={handleSubmit}
                data-testid={SESSION_TEST_IDS.composer}
                style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', minWidth: 0 }}
              >
                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={3}
                  disabled={channel.busy || watching !== undefined}
                  aria-label="Votre réponse"
                  placeholder={watching ?? 'Écrivez votre réponse…'}
                  /* The TRANSPORT's bound (`sessionMessageSchema`), enforced
                     silently: no counter and no sentence, because §9.5 sets no
                     product ceiling and a number under the field would read as
                     « faites plus court ». What it buys is that a very long
                     paste is trimmed here rather than coming back as a 400 the
                     screen has no French for. */
                  maxLength={SESSION_MESSAGE_MAX_CHARS}
                  style={{ ...FIELD, opacity: channel.busy ? 0.6 : 1 }}
                />
                <button
                  type="submit"
                  disabled={blocked || draft.trim() === ''}
                  style={{ ...BUTTON, opacity: blocked || draft.trim() === '' ? 0.6 : 1 }}
                >
                  Envoyer
                </button>
              </form>
              {watching === undefined ? <p style={HINT}>{COMPOSER_HINT}</p> : null}
            </>
          )}
          {foot === 'continue' && (
            <button
              type="button"
              data-testid={SESSION_TEST_IDS.continue}
              data-waiting={advancing ? 'true' : 'false'}
              style={{ ...BUTTON, opacity: advancing ? 0.55 : 1 }}
              onClick={onContinue}
              disabled={advancing || watching !== undefined}
            >
              {advancing ? (
                <span style={WAITING}>
                  {/* « Un instant... porte une petite icône de chargement » (maquette
                      README) - the button stays a real, disabled button. */}
                  <LoaderCircle
                    aria-hidden="true"
                    size={16}
                    className={reducedMotion ? undefined : 'animate-spin'}
                  />
                  {READER_WAITING_LABEL}
                </span>
              ) : (
                'Continuer'
              )}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

/**
 * What the footer offers in each state of the page (§9.5). Total, so a state
 * added to the channel is a build error here and not a composer left open on it.
 */
function footOf(state: SessionProtocolState): 'composer' | 'continue' | 'nothing' {
  switch (state) {
    case 'step':
      return 'composer';
    case 'farewell':
      // Nobody answers a goodbye, and « Continuer » never comes mid-reading.
      return 'nothing';
    case 'page':
      // A page played in the conversation (#186): the person only clicks.
      return 'nothing';
    case 'closed':
      return 'continue';
    case 'experience-closed':
      // The ending of §5.3 is drawn over the whole window by the player.
      return 'nothing';
    default:
      return assertNever(state, 'session protocol state');
  }
}

/**
 * One bubble.
 *
 * The avatar and the first name ride on the INTERVIEWER's side only, and only
 * when the persona is named: « un persona anonyme n'a ni nom ni identité
 * affichée » (§10.1) means the row has to be indistinguishable from one on an
 * experience that never chose a persona, so there is nothing to hide with CSS.
 */
function Bubble({
  bubble,
  firstName,
}: {
  readonly bubble: ConversationBubble;
  readonly firstName: string | null;
}) {
  if (bubble.role === 'person') {
    return (
      <div style={PERSON_ROW}>
        <p
          data-testid={SESSION_TEST_IDS.bubble}
          data-role="person"
          data-writing="false"
          {...(bubble.kind === 'click' ? { 'data-kind': 'click' } : {})}
          style={bubble.quoted === true ? { ...PERSON_BUBBLE, ...QUOTE } : PERSON_BUBBLE}
        >
          {bubble.quoted === true ? <span style={CLAMP}>{bubble.text}</span> : bubble.text}
        </p>
      </div>
    );
  }

  return (
    <div style={AI_ROW}>
      {firstName !== null && (
        <span style={AVATAR} aria-hidden="true">
          {firstName.slice(0, 1).toUpperCase()}
        </span>
      )}
      <div style={STACK}>
        {firstName !== null && <span style={NAME}>{firstName}</span>}
        <p
          data-testid={SESSION_TEST_IDS.bubble}
          data-role="interviewer"
          data-writing={String(bubble.writing)}
          {...(bubble.kind === 'bubble' ? { 'data-kind': 'bubble' } : {})}
          style={AI_BUBBLE}
        >
          {bubble.text}
        </p>
      </div>
    </div>
  );
}
